import React, { useState } from 'react';
import { Menu, Icon, Image, Dropdown } from 'semantic-ui-react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

import ParentTaskModal from './components/ParentTaskModal';
import ParentTaskEditModal from './components/ParentTaskEditModal';
import TaskGenerationModal from './components/TaskGenerationModal';
import ShareModal from './components/ShareModal';
import QRCodeModal from './components/QRCodeModal';
import DelegateRosterModal from './components/DelegateRosterModal';
import TemplateListModal from './components/TemplateListModal';
import ApiKeyModal from './components/ApiKeyModal';
import WebhookModal from './components/WebhookModal';
import { useTaskContext } from './context/TaskContext';

const menuItemStyle = {
    padding: '0.5em 0.7em',
    border: 'none !important',
    boxShadow: 'none !important',
    borderRadius: 0,
    margin: 0
};

const StyledMenu = styled(Menu)`
  &&& {
    border: none !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    margin: 0 !important;
    min-height: auto !important;
    height: 50px;
  }

  &&& .item {
    border: none !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    margin: 0 !important;
    padding: 0 0.7em !important;
    display: flex !important;
    align-items: center !important;
    height: 100% !important;

    &:before {
      display: none !important;
    }
  }
`;

const CenteredIcon = styled(Icon)`
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  height: 100% !important;
`;

const AppHeader = ({
    user,
    handleLogin,
    handleLogout,
    handleUserSettings,
    handleAssignedDidits,
    getUserDisplayName,
    addTasks,
    isCompactMode,
    toggleViewMode,
    isCreateTaskModalOpen,
    setIsCreateTaskModalOpen
}) => {
    const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
    const isParentTaskModalOpen = isCreateTaskModalOpen;
    const setIsParentTaskModalOpen = setIsCreateTaskModalOpen;
    const [isParentTaskEditModalOpen, setIsParentTaskEditModalOpen] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [isQRCodeModalOpen, setIsQRCodeModalOpen] = useState(false);
    const [isDelegateRosterOpen, setIsDelegateRosterOpen] = useState(false);
    const [isTemplateListOpen, setIsTemplateListOpen] = useState(false);
    const [isApiKeyModalOpen, setIsApiKeyModalOpen] = useState(false);
    const [isWebhookModalOpen, setIsWebhookModalOpen] = useState(false);

    const { parentTask, loadTask } = useTaskContext();
    const navigate = useNavigate();

    const hasPermission = (permission) => {
        return user && user.Permissions && user.Permissions.includes(permission);
    };

    const handleParentTaskCreated = (newParentUUID) => {
        navigate(`/?p=${newParentUUID}`);
        loadTask(newParentUUID);
    };

    const handleShareParentTask = () => {
        if (navigator.share) {
            navigator.share({
                title: parentTask?.label || 'Didit',
                text: parentTask?.label || 'Check out this task list',
                url: window.location.href,
            })
            .catch(() => setIsShareModalOpen(true));
        } else {
            setIsShareModalOpen(true);
        }
    };

    const handleOwnedDidits = () => {
        navigate('/owned');
    };

    return (
        <>
            <StyledMenu fixed="top">
                <Menu.Item as="a" header style={{
                    ...menuItemStyle,
                    padding: '0.5em',
                    paddingRight: '.5em',
                    paddingLeft: '.75em',
                    cursor: 'pointer'
                }} onClick={() => navigate('/')}>
                    <img
                        src="didit_logo_bold.svg"
                        alt="didit"
                        height={32}
                        style={{ width: 'auto', maxHeight: '100%' }}
                    />
                </Menu.Item>
                {parentTask && (
                    <Menu.Item as="a" header style={menuItemStyle} onClick={toggleViewMode}>
                        <CenteredIcon>
                            <img
                                width={24}
                                height={24}
                                src={isCompactMode ? "/detail_view.svg" : "/compact_view.svg"}
                                alt={isCompactMode ? "Switch to Detail View" : "Switch to Compact View"}
                            />
                        </CenteredIcon>
                    </Menu.Item>
                )}
                {hasPermission('create_parent_task') && (
                    <Menu.Item as="a" header style={menuItemStyle} onClick={() => setIsParentTaskModalOpen(true)}>
                        <CenteredIcon>
                            <img width={24} height={24} src="/layer-plus.svg" alt="New Didit" />
                        </CenteredIcon>
                    </Menu.Item>
                )}
                {hasPermission('ai_add_child_tasks') && (
                    <Menu.Item as="a" header style={menuItemStyle} onClick={() => setIsGenerateModalOpen(true)}>
                        <CenteredIcon>
                            <img width={24} height={24} src="ai_icon_outline.svg" alt="AI Icon" />
                        </CenteredIcon>
                    </Menu.Item>
                )}

                {parentTask && (
                    <Menu.Item as="a" header style={menuItemStyle} onClick={handleShareParentTask}>
                        <CenteredIcon>
                            <img width={24} height={24} src="share-alt-square.svg" alt="Share Task" />
                        </CenteredIcon>
                    </Menu.Item>
                )}
                {parentTask && (
                    <Menu.Item as="a" header style={menuItemStyle} onClick={() => setIsQRCodeModalOpen(true)}>
                        <CenteredIcon>
                            <img width={24} height={24} src="qr.svg" alt="QR Code" />
                        </CenteredIcon>
                    </Menu.Item>
                )}
                {user && parentTask && (parentTask.owner === user.ID || parentTask.creator === user.ID) && (
                    <Menu.Item as="a" header style={menuItemStyle} onClick={() => setIsParentTaskEditModalOpen(true)}>
                        <CenteredIcon>
                            <img width={24} height={24} src="pen-circle.svg" alt="Edit Task List" />
                        </CenteredIcon>
                    </Menu.Item>
                )}

                <Menu.Item position="right" style={menuItemStyle}>
                    {user ? (
                        <Dropdown
                            trigger={
                                <span>
                                    {user.Picture && <Image src={user.Picture} avatar />}
                                </span>
                            }
                            pointing="top right"
                            icon={null}
                        >
                            <Dropdown.Menu>
                                <Dropdown.Item>
                                    {getUserDisplayName(user)}
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item onClick={handleOwnedDidits}>
                                    <Icon>
                                        <img src="ownership.svg" alt="Owned" style={{ width: '14px', height: '14px', marginRight: '8px' }} />
                                    </Icon>
                                    Owned
                                </Dropdown.Item>
                                <Dropdown.Item onClick={handleAssignedDidits}>
                                    <Icon>
                                        <img src="responsibility.svg" alt="Assigned" style={{ width: '14px', height: '14px', marginRight: '8px' }} />
                                    </Icon>
                                    Assigned
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => setIsDelegateRosterOpen(true)}>
                                    <Icon>
                                        <img src="users-alt.svg" alt="Delegates" style={{ width: '14px', height: '14px', marginRight: '8px' }} />
                                    </Icon>
                                    Delegates
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => setIsTemplateListOpen(true)}>
                                    <img src="duplicate.svg" alt="Templates" style={{ width: '14px', height: '14px', marginRight: '8px' }} />
                                    Templates
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => setIsApiKeyModalOpen(true)}>
                                    <Icon name="key" style={{ marginRight: '8px' }} />
                                    API Keys
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => setIsWebhookModalOpen(true)}>
                                    <Icon name="bolt" style={{ marginRight: '8px' }} />
                                    Webhooks
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item onClick={handleUserSettings}>
                                    <Icon>
                                        <img src="customization-cogwheel.svg" alt="Settings" style={{ width: '14px', height: '14px', marginRight: '8px' }} />
                                    </Icon>
                                    Settings
                                </Dropdown.Item>
                                <Dropdown.Item onClick={handleLogout}>
                                    <Icon>
                                        <img src="sign-out-alt.svg" alt="Logout" style={{ width: '14px', height: '14px', marginRight: '8px' }} />
                                    </Icon>
                                    Logout
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    ) : (
                        <CenteredIcon onClick={handleLogin} style={{ cursor: 'pointer' }}>
                            <img src="circle-user-unknown.svg" alt="Login" style={{ width: '32px', height: '32px' }} />
                        </CenteredIcon>
                    )}
                </Menu.Item>
            </StyledMenu>

            <ParentTaskModal
                open={isParentTaskModalOpen}
                onClose={() => setIsParentTaskModalOpen(false)}
                onCreated={handleParentTaskCreated}
            />
            <TaskGenerationModal
                open={isGenerateModalOpen}
                onClose={() => setIsGenerateModalOpen(false)}
                onTasksGenerated={addTasks}
            />
            <ShareModal
                open={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                taskLabel={parentTask?.label}
            />
            <QRCodeModal
                open={isQRCodeModalOpen}
                onClose={() => setIsQRCodeModalOpen(false)}
            />
            {parentTask && (
                <ParentTaskEditModal
                    open={isParentTaskEditModalOpen}
                    onClose={() => setIsParentTaskEditModalOpen(false)}
                    task={parentTask}
                    onUpdated={() => loadTask(parentTask.uuid)}
                    onDeleted={() => navigate('/')}
                />
            )}
            <DelegateRosterModal
                open={isDelegateRosterOpen}
                onClose={() => setIsDelegateRosterOpen(false)}
            />
            <TemplateListModal
                open={isTemplateListOpen}
                onClose={() => setIsTemplateListOpen(false)}
                onClone={(newTask) => {
                    navigate(`/?p=${newTask.uuid}`);
                    loadTask(newTask.uuid);
                }}
            />
            <ApiKeyModal
                open={isApiKeyModalOpen}
                onClose={() => setIsApiKeyModalOpen(false)}
            />
            <WebhookModal
                open={isWebhookModalOpen}
                onClose={() => setIsWebhookModalOpen(false)}
            />
        </>
    );
};

export default AppHeader;
